import { FieldsOptions } from '@sim/form';
import { FilterFieldsOptions } from '@ui/ui.module';

import { iccdDateValidator } from '@sim/form/validators';

import { CardRelation, CardRelationMeta, ICCDMeta } from '@atmi/core/models/cards/card';
import { RelationDMeta, RelationFMeta, RelationMIMeta, RelationOAMeta, RelationSMeta } from '@atmi/core/models/cards/relation';

export interface MIS {
  /** Tipo di misure */
  MISO: string;
  /** Unità */
  MISU: string;
  /** Altezza */
  MISA: string;
  /** Larghezza */
  MISL: string;
  /** Diametro */
  MISD: string;
  /** Validità */
  MIST: string;
  /** Spessore */
  MISS: string;
}

export interface LRC {
  /** Stato */
  LRCS: string;
  /** Regione */
  LRCR: string;
  /** Provincia */
  LRCP: string;
  /** Comune */
  LRCC: string;
  /** Località */
  LRCL: string;
  /** Località estera */
  LRCE: string;
}

export interface CPR {
  /** Nome */
  CPRN: string;
  /** Indirizzo */
  CPRI: string;
  /** Data di scadenza */
  CPRD: string;
  /** Note */
  CPRS: string;
}

export interface ISE {
  /** Riferimento alla parte */
  ISER: string;
  /** Posizione (*) */
  ISEP: string;
  /** Definizione (*) */
  ISED: string;
  /** Specifiche */
  ISEE: string;
  /** Descrizione */
  ISEZ: string;
  /** Quantità */
  ISEQ: string;
  /** Supporto */
  ISES: string;
  /** Classe di appartenenza */
  ISEC: string;
  /** Lingua */
  ISEL: string;
  /** Alfabeto */
  ISEF: string;
  /** Tipo di caratteri */
  ISET: string;
  /** Materia e tecnica */
  ISEM: string;
  /** Trascrizione */
  ISEI: string;
  /** Traduzione */
  ISEB: string;
  /** Autore */
  ISEA: string;
  /** Identificazione */
  ISEO: string;
  /** Note */
  ISEN: string;
}

export interface MTC {
  /** Riferimento alla parte */
  MTCP: string;
  /** Materia */
  MTCM: string;
  /** Tecnica */
  MTCT: string;
  /** Note */
  MTCS: string;
}

export interface FVC {
  /** Formato di memorizzazione */
  FVCF: string;
  /** Programma di memorizzazione */
  FVCP: string;
  /** Profondità di colore e metodo di memorizzazione */
  FVCC: string;
  /** Unità di misura */
  FVCU: string;
  /** Misure fotografia virtuale */
  FVCM: string;
  /** Note */
  FVCN: string;
}

export interface UBF {
  /** Fondo */
  UBFP: string;
  /** Collocazione */
  UBFC: string;
}

export interface CardF extends CardRelation {
  /** Misure */
  MIS: MIS;
  MTX: string;
  FIL: string;
  /** Localizzazione */
  LRC: LRC;
  /** Occasione */
  LRO: string;
  /** Data della ripresa */
  LRD: string;
  /** Diritti d'autore */
  CPR: CPR[];
  /** Iscrizioni */
  ISE: ISE[];
  /** Materiali e tecniche */
  // TODO: Enable MTC: MTC[];
  /** Caratteristica fotografia digitale */
  FVC: FVC;
  /** Ubicazione foto */
  UBF: UBF;
}

export const LRCMeta: FieldsOptions<LRC> = {
  LRCC: {},
  LRCE: {},
  LRCL: {},
  LRCP: {},
  LRCR: {},
  LRCS: {},
};

export const ISEMeta: FieldsOptions<ISE> = {
  ISEA: {},
  ISEB: {},
  ISEC: {},
  ISED: {},
  ISEE: {},
  ISEF: {},
  ISEI: {},
  ISEL: {},
  ISEM: {},
  ISEN: {},
  ISEO: {},
  ISEP: {},
  ISEQ: {},
  ISER: {},
  ISES: {},
  ISET: {},
  ISEZ: {},
};

export const CPRMeta: FieldsOptions<CPR> = {
  CPRD: { value: '' },
  CPRI: { value: '' },
  CPRN: { value: '' },
  CPRS: { value: '' },
};

export const MTCMeta: FieldsOptions<MTC> = {
  MTCM: { value: '' },
  MTCP: { value: '' },
  MTCS: { value: '' },
  MTCT: { value: '' },
};

export const MISMeta: FieldsOptions<MIS> = {
  MISA: { value: '' },
  MISD: { value: '' },
  MISL: { value: '' },
  MISO: { value: '' },
  MISS: { value: '' },
  MIST: { value: '', validators: [iccdDateValidator()] },
  MISU: { value: 'cm' },
};

export const FVCMeta: FieldsOptions<FVC> = {
  FVCC: { value: '' },
  FVCF: { value: '' },
  FVCM: { value: '' },
  FVCN: { value: '' },
  FVCP: { value: '' },
  FVCU: { value: '' },
};

export const UBFMeta: FieldsOptions<UBF> = {
  UBFC: { value: '' },
  UBFP: { value: '' },
};

export const CardFMeta: FieldsOptions<any> = {
  ...CardRelationMeta,
  CPR: { type: 'array', controls: CPRMeta, value: [], additionalInfo: { label: 'Diritti d\'autore' } },
  FIL: { value: '', additionalInfo: { single: true } },
  FVC: { type: 'object', controls: FVCMeta, value: {}, additionalInfo: { label: 'Caratteristica fotografia digitale' } },
  ISE: { type: 'array', controls: ISEMeta, value: [], additionalInfo: { label: 'Iscrizioni' } },
  LRC: { type: 'object', controls: LRCMeta, value: {}, additionalInfo: { label: 'Localizzazione' } },
  LRD: { value: '', additionalInfo: { single: true }, validators: [iccdDateValidator()] },
  LRO: { value: '', additionalInfo: { single: true } },
  MIS: { type: 'object', controls: MISMeta, value: {}, additionalInfo: { label: 'Misure' } },
  MTC: { type: 'array', controls: MTCMeta, value: [], additionalInfo: { label: 'Materie e tecniche' } },
  MTX: { value: '', additionalInfo: { single: true } },
  TSK: { ...ICCDMeta.TSK, value: 'F' },
  UBF: { type: 'object', controls: UBFMeta, additionalInfo: { label: 'Ubicazione' } },
  relazioniBeniD: { type: 'array', controls: RelationDMeta, value: [] },
  relazioniBeniF: { type: 'array', controls: RelationFMeta, value: [] },
  relazioniBeniMI: { type: 'array', controls: RelationMIMeta, value: [] },
  relazioniBeniOA: { type: 'array', controls: RelationOAMeta, value: [] },
  relazioniBeniS: { type: 'array', controls: RelationSMeta, value: [] },
};

export interface CardFFilter {
  codiceUnico?: string;
  ['SGT.SGTI']?: string;
  ['OGT.OGTD']?: string;
  ['CPR.CPRN']?: string;
  ['FVC.FVCF']?: string;
  ['FVC.FVCU']?: string;
  ['FVC.FVCM']?: string;
  ['MIS.MISA']?: string;
  ['MIS.MISL']?: string;
  LRO?: string;
  MTX?: string;
  LRD?: string;
}

export const CardFFiltersMeta: FilterFieldsOptions<CardFFilter> = {
  LRD: { operator: 'begins_with' },
  LRO: { operator: 'contains' },
  MTX: { operator: 'contains' },
  codiceUnico: { operator: 'contains' },
  ['CPR.CPRN']: { operator: 'contains' },
  ['SGT.SGTI']: { operator: 'contains' },
  ['OGT.OGTD']: { operator: 'contains' },
  ['FVC.FVCF']: { operator: 'contains' },
  ['FVC.FVCU']: { operator: 'contains' },
  ['FVC.FVCM']: { operator: 'contains' },
  ['MIS.MISA']: { operator: 'begins_with' },
  ['MIS.MISL']: { operator: 'begins_with' },
};
